// src/features/workspace/common/components/feedback/OperationErrorAlert.tsx
//
// A destructive-styled inline alert with three regions:
//
//   • Title    — short, user-friendly headline ("Can't sample distinct
//                values from this JSON property")
//   • Body     — optional one-paragraph explanation with action guidance
//                (renders inline `code` for backticked spans so DBA-facing
//                SQL like `ALTER DATABASE …` stays scannable)
//   • Details  — the raw error string, hidden behind a "Show details"
//                toggle so the headline doesn't compete with a wall of
//                driver-error text. Toggle uses an aria-expanded button
//                and a chevron icon for affordance.
//
// Designed to replace inline "{ errorMessage }" raw-text dumps in error
// banners across the workspace. Today it backs DeclareEnumColumnDialog and
// EnumValuesDetailsDialog; it's component-agnostic so other surfaces can
// adopt the same shape without duplicating it.

import { useId, useState } from 'react';
import { ChevronRight } from 'lucide-react';

type OperationErrorAlertProps = {
  /** Short, user-friendly headline (what couldn't be done). */
  title: string;
  /**
   * Optional one-paragraph explanation. Backtick spans like `ALTER DATABASE`
   * are rendered as inline `<code>` so SQL stays visually distinct from
   * surrounding prose. Use sparingly — only when we can give an actionable
   * "why this can't be done and what to do about it" sentence.
   */
  description?: string | null;
  /** Raw error string from the underlying call. Surfaced in the collapsible. */
  details: string;
  /** Optional test-id passthrough. Sub-elements derive their ids from it. */
  testId?: string;
};

/** Render a description string with backticked spans turned into `<code>`. */
function renderDescription(description: string): React.ReactNode {
  const parts = description.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={i}
          translate="no"
          className="font-mono text-[10.5px] rounded bg-destructive/15 px-1 py-0.5"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export function OperationErrorAlert({
  title,
  description,
  details,
  testId,
}: OperationErrorAlertProps) {
  const [open, setOpen] = useState(false);
  const reactId = useId();
  const detailsId = testId ? `${testId}-details` : `op-err-details-${reactId}`;
  const hasDetails = details.trim().length > 0;

  return (
    <div
      role="alert"
      data-testid={testId}
      className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive space-y-1.5"
    >
      <div
        className="font-medium"
        data-testid={testId ? `${testId}-title` : undefined}
      >
        {title}
      </div>
      {description ? (
        <div
          className="text-destructive/85 leading-snug"
          data-testid={testId ? `${testId}-description` : undefined}
        >
          {renderDescription(description)}
        </div>
      ) : null}
      {hasDetails ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={detailsId}
          className="inline-flex items-center gap-1 text-[11px] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          <ChevronRight
            className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
          {open ? 'Hide details' : 'Show details'}
        </button>
      ) : null}
      {open && hasDetails ? (
        <pre
          id={detailsId}
          className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-destructive/5 p-2 font-mono text-[11px] text-destructive/90"
          data-testid={testId ? `${testId}-details` : undefined}
          translate="no"
        >
          {details}
        </pre>
      ) : null}
    </div>
  );
}
